import type { ApiResponse, PageResult } from "@hydeck/mdt-contracts";
import { runtimeConfig } from "../config";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  token?: string | null;
  query?: Record<string, string | number | boolean | undefined>;
};

function makeUrl(
  origin: string,
  path: string,
  query?: Record<string, string | number | boolean | undefined>,
) {
  const url = new URL(path, origin.endsWith("/") ? origin : `${origin}/`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }
  return url;
}

async function requestFrom<T>(
  origin: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");

  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (options.token) {
    headers.set("Authorization", `Bearer ${options.token}`);
  }

  const response = await fetch(makeUrl(origin, path, options.query), {
    ...options,
    headers,
    body:
      options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  const payload = (await response.json().catch(() => null)) as ApiResponse<T> | null;
  if (!response.ok || !payload?.success) {
    const rawMessage =
      payload && !payload.success ? payload.message : response.statusText;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join("\n")
      : rawMessage || "The MDT request failed";
    throw new ApiError(message, response.status);
  }
  return payload.data;
}

export function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return requestFrom<T>(runtimeConfig.apiOrigin, path, options);
}

export function authRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return requestFrom<T>(runtimeConfig.authOrigin, path, options);
}

export function apiPage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<PageResult<T>> {
  return apiRequest<PageResult<T>>(path, options);
}
